// src/pages/Dashboard.jsx
import React, { useState, useMemo, useRef, memo, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { LayoutDashboard, ChevronLeft, ChevronRight, Globe } from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { useNavigate, useParams } from "react-router-dom";
import { apiFetch, ensureAuthOrRedirect,  redirectToLogin, attachExpiryTimer, getToken } from "../utils/auth";
import { MODAL_CONFIG } from "../utils/errors";
import FrostedModal from "../components/modals/FrostedModal";
/* ===========================
   Helpers (module scope)
   =========================== */

const DASHBOARD_API = "https://whiteintel.io/api/regular/app/v2/dashboard/dashboard_stats_v3.php";
// Returns a per-domain summary for the watchlist landing table:
// [{ domain, last_event_at, last_corporate_event_at, last_consumer_event_at, total_leaks }]
const WATCHLISTS_API = "https://whiteintel.io/api/regular/app/v2/watchlist/get_dashboard_domains.php";


// Compact, enterprise-y Stat Card
// Tiny inline sparkline — lightweight SVG polyline + area fill. Sized to
// fit the bottom of a KPI card without dominating. For empty or all-zero
// data it draws a single muted flat line at the vertical midpoint, so the
// card still reads as "we have a metric here, it's just quiet" instead of
// collapsing the layout.
function Sparkline({ data, color = "#38bdf8", width = 110, height = 26 }) {
  const padY = 2;
  const midY = height / 2;

  // Flat-line placeholder for no-data or all-zero series.
  const flatLine = (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden
      className="block shrink-0"
    >
      <line
        x1="0"
        y1={midY}
        x2={width}
        y2={midY}
        stroke={color}
        strokeWidth="1.25"
        strokeOpacity="0.35"
        strokeLinecap="round"
        strokeDasharray="2 3"
      />
    </svg>
  );

  if (!Array.isArray(data) || data.length < 2) return flatLine;

  const values = data.map((d) => Number(d?.count ?? d?.value ?? 0));
  const max = Math.max(...values);
  const min = Math.min(...values);

  // All values are zero → muted dashed flat line in the middle.
  if (max === 0 && min === 0) return flatLine;

  const range = Math.max(1, max - min);
  const step = width / (values.length - 1);
  const yFor = (v) => padY + (height - padY * 2) * (1 - (v - min) / range);

  const pointsArr = values.map((v, i) => [i * step, yFor(v)]);
  const linePoints = pointsArr.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  // Closed polygon for the area fill (line points + drop to baseline)
  const areaPoints =
    pointsArr.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ") +
    ` ${width},${height} 0,${height}`;
  const gradId = `spark-${color.replace(/[^a-z0-9]/gi, "")}`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden
      className="block shrink-0"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.32} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill={`url(#${gradId})`} />
      <polyline
        points={linePoints}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function StatCard({ title, info, value, trend, loading, viewAll, onViewAll, sparkData, sparkColor = "#38bdf8" }) {
  return (
    <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 px-4 py-3.5 flex flex-col">
      {/* Uppercase eyebrow label — Datadog/Splunk pattern. The optional
          `info` line sits below in a quieter tone. */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-[11px] uppercase tracking-wider text-neutral-500 font-medium">
            {title}
          </h3>
          {info && (
            <p className="mt-0.5 text-[11px] text-neutral-600 leading-snug truncate" title={info}>
              {info}
            </p>
          )}
        </div>
        {viewAll && (
          <button
            type="button"
            onClick={onViewAll}
            className="shrink-0 text-[11px] text-sky-400 hover:text-sky-300 transition-colors"
          >
            View →
          </button>
        )}
      </div>

      <div className="mt-3 flex items-end justify-between gap-3">
        <div className="min-w-0 flex-1">
          {loading ? (
            <div className="h-7 w-20 bg-neutral-800/80 rounded animate-pulse" />
          ) : (
            <div className="text-[28px] leading-none font-semibold text-neutral-100 tabular-nums">
              {value}
            </div>
          )}
          {trend && !loading && (
            <div className={`mt-1.5 inline-flex items-center gap-1 text-[11.5px] ${trend.color}`}>
              {trend.icon}
              <span className="tabular-nums">{trend.label}</span>
            </div>
          )}
        </div>

        {/* Sparkline always renders when not loading — it draws a muted
            flat line for empty or all-zero data so every card has the same
            visual footprint. */}
        {!loading && <Sparkline data={sparkData} color={sparkColor} />}
      </div>
    </div>
  );
}

function getJwtToken() {
  try {
    return (
      localStorage.getItem("jwtToken") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("jwtToken") ||
      window.WHITEINTEL_TOKEN ||
      ""
    );
  } catch {
    return "";
  }
}

// Renders "3 days ago" with the absolute timestamp underneath in dimmer
// type. Used in the dashboard landing table so analysts can scan recency
// at a glance but still see the actual datetime if they need it.
function RelativeAndAbsolute({ iso, muted = false }) {
  if (!iso) return <span className="text-neutral-600">—</span>;
  // The backend sends "YYYY-MM-DD HH:MM:SS" without a TZ marker. Treat it
  // as UTC by appending "Z" — Date.parse() otherwise interprets it as local
  // time which would skew the relative label.
  const parsable = typeof iso === "string" && /^\d{4}-\d{2}-\d{2} /.test(iso)
    ? iso.replace(" ", "T") + "Z"
    : iso;
  const t = Date.parse(parsable);
  if (Number.isNaN(t)) return <span className="text-neutral-600">—</span>;
  const diffMs = Date.now() - t;
  const min = Math.floor(diffMs / 60000);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  let rel;
  if (min < 1) rel = "just now";
  else if (min < 60) rel = `${min}m ago`;
  else if (hr < 24) rel = `${hr}h ago`;
  else if (day < 30) rel = `${day}d ago`;
  else if (day < 365) rel = `${Math.floor(day / 30)}mo ago`;
  else rel = `${Math.floor(day / 365)}y ago`;
  const abs = new Date(t).toISOString().slice(0, 10);
  return (
    <span className="inline-flex flex-col leading-tight">
      <span className={muted ? "text-neutral-400" : "text-neutral-100"}>{rel}</span>
      <span className="text-[10.5px] text-neutral-600">{abs}</span>
    </span>
  );
}

function combineTimeline(raw) {
  // Accept both PascalCase ({Year, Month, Count}) and lowercase
  // ({year, month, count}/{value}/{total}) — different backend endpoints
  // return different shapes and a key mismatch here silently zeroes out
  // every bucket, which is what turned "Infected IPs" into "no activity".
  const byMonth = new Map();
  for (const row of raw || []) {
    if (!row) continue;
    const Year = row.Year ?? row.year;
    const Month = row.Month ?? row.month;
    const Count = row.Count ?? row.count ?? row.value ?? row.total ?? 0;
    if (Year == null || Month == null) continue;
    const key = `${Year}-${String(Month).padStart(2, "0")}`;
    byMonth.set(key, (byMonth.get(key) || 0) + Number(Count || 0));
  }
  return Array.from(byMonth.entries())
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

function combineTwoTimelines(tlA = [], tlB = []) {
  const map = new Map();
  const bump = (d, v) => map.set(d, (map.get(d) || 0) + (Number(v) || 0));
  for (const { date, count } of tlA) bump(date, count);
  for (const { date, count } of tlB) bump(date, count);
  return Array.from(map.entries())
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

// Normalizers
const pickNumber = (obj) => {
  if (obj == null) return 0;
  if (typeof obj === "number") return obj;
  if (typeof obj === "string" && !isNaN(Number(obj))) return Number(obj);
  if (Array.isArray(obj)) return obj.length;

  if (typeof obj === "object") {
    if (typeof obj.count === "number") return obj.count;
    if (typeof obj.value === "number") return obj.value;
    if (obj.data && typeof obj.data.count === "number") return obj.data.count;
    if (obj.total && typeof obj.total === "number") return obj.total;
    if (Array.isArray(obj.items)) return obj.items.length;
    if (Array.isArray(obj.data)) return obj.data.length;
    const firstNumeric = Object.values(obj).find((v) => typeof v === "number");
    if (typeof firstNumeric === "number") return firstNumeric;
  }
  return 0;
};

const pickArray = (obj) => {
  if (Array.isArray(obj)) return obj;
  if (obj && Array.isArray(obj.items)) return obj.items;
  if (obj && Array.isArray(obj.data)) return obj.data;
  if (obj && Array.isArray(obj.timeline)) return obj.timeline;
  return [];
};

// Safe POST wrapper


// “This month” trend (▲ red only if >0, else ↔ stable)
// “This month” trend:
// - If there are new records in the current month → ▲ +X since {Mon YYYY}
// - Otherwise → ↔ Stable since {last active Mon YYYY} (the most recent month with count > 0)
// - If there's never been activity → ↔ No activity observed
function computeThisMonthNewRecords(timeline = [], { now = new Date() } = {}) {
  // If the backend didn't return a timeline for this metric, suppress the
  // chip entirely. Better blank than the misleading "No activity observed"
  // sitting under a non-zero KPI value.
  if (!Array.isArray(timeline) || timeline.length === 0) {
    return { current: 0, trendUi: null };
  }

  // Normalize + sort
  const data = [...timeline]
    .map(d => ({ date: String(d.date), count: Number(d.count) || 0 }))
    .sort((a, b) => a.date.localeCompare(b.date));

  // Build quick lookup
  const map = new Map(data.map(d => [d.date, d.count]));

  // Current month key
  const currKey = ymKey(now.getFullYear(), now.getMonth() + 1);
  const current = map.get(currKey) || 0;

  if (current > 0) {
    // New activity this month
    return {
      current,
      trendUi: upUi(`+${current.toLocaleString()} since ${monthYear(now)}`),
    };
  }

  // Find the most recent month with count > 0
  let lastActiveKey = null;
  for (let i = data.length - 1; i >= 0; i--) {
    if (data[i].count > 0) {
      lastActiveKey = data[i].date;
      break;
    }
  }

  if (!lastActiveKey) {
    // Timeline rows came through but all entries are zero — usually means
    // the API returned a shape we don't recognize. Hide the chip rather
    // than print "No activity observed" next to a non-zero count.
    return { current: 0, trendUi: null };
  }

  const { y, m } = keyToParts(lastActiveKey);
  const lastActiveLabel = monthYear(new Date(y, m - 1, 1));

  return {
    current: 0,
    trendUi: stableUi(`Stable since ${lastActiveLabel}`),
  };

  // helpers



  function ymKey(y, m) { return `${y}-${String(m).padStart(2, "0")}`; }
  function keyToParts(k) { const [yy, mm] = k.split("-").map(Number); return { y: yy, m: mm }; }
  function monthYear(date) { return date.toLocaleString("en-US", { month: "short", year: "numeric" }); }
  function upUi(text)     { return { color: "text-red-500",     icon: "▲", label: (<>{text}</>) }; }
  function stableUi(text) { return { color: "text-neutral-400", icon: "↔", label: (<>{text}</>) }; }
}



function typePillClass(t) {
  const k = String(t || "").toLowerCase();
  // Tints align with the Exposure Breakdown palette so an analyst's
  // eye carries one color language across the page.
  if (k === "stealer") return "bg-rose-500/10 text-rose-300 ring-rose-500/20";
  if (k === "combolist") return "bg-amber-500/10 text-amber-300 ring-amber-500/20";
  return "bg-cyan-500/10 text-cyan-300 ring-cyan-500/20";
}
const formatNumber = (n) => (n ?? 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// Google's S2 service: returns a real favicon for known domains and a
// generic globe glyph for unknowns, so we mostly don't need our own fallback —
// onError still covers actual network failures.
function DomainFavicon({ domain, size = 16, className = "" }) {
  const [failed, setFailed] = useState(false);
  if (!domain || failed) {
    return (
      <Globe
        aria-hidden
        className={`shrink-0 text-neutral-500 ${className}`}
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=${size * 2}`}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className={`shrink-0 rounded-sm ${className}`}
    />
  );
}

function TableSkeletonRows({ rows = 8 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={`s-${i}`} className="border-b border-neutral-800">
          <td className="py-2 px-3"><div className="h-4 w-56 bg-neutral-800/80 rounded animate-pulse" /></td>
          <td className="py-2 px-3">
            <div className="h-4 w-16 bg-neutral-800/80 rounded animate-pulse" />
          </td>
          <td className="py-2 px-3"><div className="h-4 w-24 bg-neutral-800/80 rounded animate-pulse" /></td>
          <td className="py-2 px-3 text-right"><div className="ml-auto h-4 w-12 bg-neutral-800/80 rounded animate-pulse" /></td>
        </tr>
      ))}
    </>
  );
}

// Stable tooltip element
const DarkTip = memo(function DarkTipInner({ active, payload, label }) {
  return active && payload?.length ? (
    <div className="rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-xs text-neutral-200 shadow-xl">
      <div className="mb-1 text-neutral-400">{label}</div>
      <div className="font-semibold text-white">{payload[0].value}</div>
    </div>
  ) : null;
});

// Merge two `{date, count}` series into one array shaped for a multi-series
// AreaChart: `[{date, corp, cons}, ...]`. Both series share the same X axis.
function mergeTwoSeries(seriesA = [], seriesB = [], keyA = "a", keyB = "b") {
  const byDate = new Map();
  for (const r of seriesA || []) {
    if (!r?.date) continue;
    if (!byDate.has(r.date)) byDate.set(r.date, { date: r.date, [keyA]: 0, [keyB]: 0 });
    byDate.get(r.date)[keyA] += Number(r.count || 0);
  }
  for (const r of seriesB || []) {
    if (!r?.date) continue;
    if (!byDate.has(r.date)) byDate.set(r.date, { date: r.date, [keyA]: 0, [keyB]: 0 });
    byDate.get(r.date)[keyB] += Number(r.count || 0);
  }
  return Array.from(byDate.values()).sort((a, b) =>
    a.date < b.date ? -1 : a.date > b.date ? 1 : 0
  );
}

// IncidentTimelineCard — one chart with two stacked area series (corporate
// + consumer). Replaces the two side-by-side AreaCards; same X-axis means
// the analyst can compare directly instead of eye-flicking between charts.
const IncidentTimelineCard = memo(function IncidentTimelineCard({
  corporateData = [],
  consumerData = [],
  loading = false,
}) {
  const merged = mergeTwoSeries(corporateData, consumerData, "corporate", "consumer");
  const hasData = merged.length > 0;

  // Toggleable legend — click a chip to hide that series. If both end up
  // hidden, click the same chip again to bring it back. Matches the
  // legend-as-toggle pattern Datadog / Grafana use.
  const [series, setSeries] = useState({ corporate: true, consumer: true });
  const toggle = (key) => setSeries((s) => ({ ...s, [key]: !s[key] }));
  const SeriesChip = ({ k, label, color }) => {
    const on = series[k];
    return (
      <button
        type="button"
        onClick={() => toggle(k)}
        className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded transition-colors ${
          on
            ? "text-neutral-300 hover:text-neutral-100"
            : "text-neutral-600 hover:text-neutral-400"
        }`}
        aria-pressed={on}
      >
        <span
          className="inline-block h-2 w-2 rounded-sm"
          style={{ background: color, opacity: on ? 1 : 0.35 }}
        />
        <span className={on ? "" : "line-through"}>{label}</span>
      </button>
    );
  };

  return (
    <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 flex flex-col overflow-hidden">
      <div className="px-4 py-2.5 border-b border-sky-900/30 bg-[#0b1426]/60 flex items-center justify-between gap-3">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
          Incident Timeline
        </span>
        <div className="flex items-center gap-1 text-[10.5px]">
          <SeriesChip k="corporate" label="Corporate" color="#facc15" />
          <SeriesChip k="consumer" label="Consumer" color="#60a5fa" />
        </div>
      </div>

      <div className="h-64 p-3 pl-0">
        {loading ? (
          <SkeletonChart />
        ) : !hasData ? (
          <div className="flex items-center justify-center h-full text-[12.5px] text-neutral-500">
            No timeline data for this asset.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%" debounce={200}>
            <AreaChart data={merged} margin={{ top: 10, right: 12, left: 12, bottom: 0 }}>
              <defs>
                <linearGradient id="gradCorporate" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#facc15" stopOpacity={0.28} />
                  <stop offset="95%" stopColor="#facc15" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradConsumer" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.28} />
                  <stop offset="95%" stopColor="#60a5fa" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fill: "#94a3b8", fontSize: 10, fontFamily: "Outfit" }}
                axisLine={false}
                tickLine={false}
                minTickGap={28}
              />
              <YAxis
                tick={{ fill: "#94a3b8", fontSize: 10, fontFamily: "Outfit" }}
                axisLine={false}
                tickLine={false}
                width={36}
              />
              <Tooltip
                content={<MultiSeriesTip />}
                cursor={{ stroke: "rgba(148,163,184,0.25)", strokeDasharray: "3 3" }}
              />
              {/* Corporate sits behind, Consumer in front — so the brighter
                  blue line stays legible when the two overlap. Each Area
                  only renders if its legend chip is on. */}
              {series.corporate && (
                <Area
                  type="monotone"
                  dataKey="corporate"
                  name="Corporate"
                  stroke="#facc15"
                  strokeWidth={2}
                  fill="url(#gradCorporate)"
                  fillOpacity={1}
                />
              )}
              {series.consumer && (
                <Area
                  type="monotone"
                  dataKey="consumer"
                  name="Consumer"
                  stroke="#60a5fa"
                  strokeWidth={2}
                  fill="url(#gradConsumer)"
                  fillOpacity={1}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
});

// Two-series tooltip — labelled, monospace numbers for alignment.
const MultiSeriesTip = memo(function MultiSeriesTipInner({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-sky-900/40 bg-[#0a1224] px-3 py-2 text-[12px] text-neutral-200 shadow-xl">
      <div className="mb-1 text-neutral-500">{label}</div>
      <div className="space-y-0.5">
        {payload.map((p) => (
          <div key={p.dataKey} className="flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-sm" style={{ background: p.color }} />
              <span className="text-neutral-300">{p.name}</span>
            </span>
            <span className="tabular-nums font-medium text-white">{p.value?.toLocaleString?.() ?? p.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
});

// Shared chart skeleton — used by both AreaCard and IncidentTimelineCard.
function SkeletonChart() {
  return (
    <div className="h-full pl-4 pr-3 pt-2 pb-1 flex items-end gap-1.5">
      {Array.from({ length: 24 }).map((_, i) => {
        const h = 30 + ((i * 17) % 60);
        return (
          <div
            key={i}
            className="flex-1 rounded-sm bg-neutral-800/80 animate-pulse"
            style={{ height: `${h}%` }}
          />
        );
      })}
    </div>
  );
}

// AreaCard — single-series fallback (kept in case anything else still
// imports the prior shape). The dashboard itself now uses
// IncidentTimelineCard.
const AreaCard = memo(function AreaCard({ title, data, color = "#3b82f6", loading = false }) {
  const hasData = Array.isArray(data) && data.length > 0;
  // Deterministic gradient id per color so multiple AreaCards on the same
  // page don't collide on shared <defs> IDs.
  const gradId = `area-${String(color).replace(/[^a-z0-9]/gi, "")}`;
  return (
    <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 flex flex-col overflow-hidden">
      {/* Header strip */}
      <div className="px-4 py-2.5 border-b border-sky-900/30 bg-[#0b1426]/60">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
          {title}
        </span>
      </div>

      <div className="h-64 p-3 pl-0">
        {loading ? (
          /* Skeleton — a column of pulsing bars approximating the area
              chart's silhouette so the layout doesn't shift when data lands. */
          <div className="h-full pl-4 pr-3 pt-2 pb-1 flex items-end gap-1.5">
            {Array.from({ length: 24 }).map((_, i) => {
              // Deterministic pseudo-random heights — gives a chart-like
              // silhouette without using Math.random (which would cause
              // hydration churn).
              const h = 30 + ((i * 17) % 60);
              return (
                <div
                  key={i}
                  className="flex-1 rounded-sm bg-neutral-800/80 animate-pulse"
                  style={{ height: `${h}%` }}
                />
              );
            })}
          </div>
        ) : !hasData ? (
          <div className="flex items-center justify-center h-full text-[12.5px] text-neutral-500">
            No data available for the specified query.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%" debounce={200}>
            <AreaChart data={data} margin={{ top: 10, right: 12, left: 12, bottom: 0 }}>
              <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.28} />
                  <stop offset="95%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fill: "#94a3b8", fontSize: 10, fontFamily: "Outfit" }}
                axisLine={false}
                tickLine={false}
                minTickGap={28}
              />
              <YAxis
                tick={{ fill: "#94a3b8", fontSize: 10, fontFamily: "Outfit" }}
                axisLine={false}
                tickLine={false}
                width={36}
              />
              <Tooltip content={<DarkTip />} cursor={{ stroke: "rgba(148,163,184,0.25)", strokeDasharray: "3 3" }} />
              <Area
                type="monotone"
                dataKey="count"
                stroke={color}
                strokeWidth={2}
                fill={`url(#${gradId})`}
                fillOpacity={1}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
});

/* ===========================
   Component
   =========================== */

export default function Dashboard() {
    
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [active, setActive] = useState("overview");
  const [selectedWatchlist, setSelectedWatchlist] = useState(null);

  const [dashError, setDashError] = useState("");

  const navigate = useNavigate();
  // URL-driven domain scope: /dashboard/:domain auto-loads the page for
  // that vendor on mount or refresh. The empty `/dashboard` path falls
  // back to the watchlist landing.
  const { domain: routeDomain } = useParams();

function onViewAll(key) {
    let params = new URLSearchParams();

    if (key === "consumer") {
      params.set("type", "consumerRecords");
    } else if (key === "corporate") {
      params.set("type", "corporateRecords");
    }

    if (navigateQuery) {
      params.set("query", navigateQuery);
    }

    navigate(`/search?${params.toString()}`);
}

  // loading flags per metric (so each card/table updates independently)
  const [loading, setLoading] = useState({
    consumerCount: false,
    corporateCount: false,
    consTL: false,
    corpTL: false,
    latestCE: false,
    latestCoE: false,
  });
  const setL = (k, v) => setLoading(prev => ({ ...prev, [k]: v }));

  // Big numbers
  // KPI counts are stored as { floor, capped } so we can both sum across
  // sources and render a "+" suffix when the backend hits its 100K cap.
  const [consumerCount, setConsumerCount] = useState({ floor: 0, capped: false });
  const [corporateCount, setCorporateCount] = useState({ floor: 0, capped: false });
  // Source-level breakdown for the Recent Exposure card. Each source is
  // also { floor, capped }. The breakdown bar proportions use floor.
  // Dark-web mentions sit alongside credential sources here so analysts
  // see *all* surfaces where the asset shows up in one place.
  const [exposureBreakdown, setExposureBreakdown] = useState({
    combolist: { floor: 0, capped: false },
    malware: { floor: 0, capped: false },
    database: { floor: 0, capped: false },
  });
  const [applicationCount, setApplicationCount] = useState({ floor: 0, capped: false });
  const [thirdPartyApplicationCount, setThirdPartyApplicationCount] = useState({ floor: 0, capped: false });
  // Dark-web visibility: count from tier1, list from tier2.
  const [darkWebMentionsCount, setDarkWebMentionsCount] = useState({ floor: 0, capped: false });
  const [latestDarkWebMentions, setLatestDarkWebMentions] = useState([]);
  const [modal, setModal] = useState({ open: false, type: null });
    const openModalFor = (type) => setModal({ open: true, type });
    const closeModal = () => setModal({ open: false, type: null });
    const cfg = modal.type ? MODAL_CONFIG[modal.type] : null;
  // Timelines (state used for counts/trends), but charts will use FROZEN refs
  const [consumerIncidentTimeline, setConsumerIncidentTimeline] = useState([]);
  const [corporateIncidentTimeline, setCorporateIncidentTimeline] = useState([]);
  const [consRaw, setConsRaw] = useState([]);
  const [corpRaw, setCorpRaw] = useState([]);
  const [wlLoading, setWlLoading] = useState(true);
const [wlError, setWlError] = useState("");
const [watchlistQueries, setWatchlistQueries] = useState([]); // [{id,label}]

  // Frozen chart data (filled once per search)
  const corpChartRef = useRef([]);
  const consChartRef = useRef([]);
  const [chartTick, setChartTick] = useState(0); // force single repaint when we set frozen data

  // Events
  const [latestConsumerEvents, setLatestConsumerEvents] = useState([]);
  const [latestCorporateEvents, setLatestCorporateEvents] = useState([]);

  // Tabs / search
  const [activeTab, setActiveTab] = useState("consumer");
  const [query, setQuery] = useState("");
  const [hasSearchQuery, setHasSearchQuery] = useState(null);
  // Carries the row metadata when a user clicks a watchlist row, so the
  // detail header can show "watchlisted since / last event / total leaks"
  // alongside the domain. Set to `{ domain }` only for ad-hoc lookups.
  const [selectedDomainMeta, setSelectedDomainMeta] = useState(null);
  // Surface load failures as a modal + bounce back to the landing so the
  // analyst isn't stuck on a half-loaded detail view.
  const [errorModal, setErrorModal] = useState(null); // { domain, message } | null
  // Pagination for the landing watchlist table — driven by the server.
  // The backend returns `{ data: [...], pagination: { page, per_page,
  // total, total_pages } }`, so the table rows we render are exactly one
  // page's worth and the totals come from the response.
  const WATCHLIST_PAGE_SIZE = 20;
  const [wlPage, setWlPage] = useState(1);
  const [wlPagination, setWlPagination] = useState({
    page: 1,
    per_page: WATCHLIST_PAGE_SIZE,
    total: 0,
    total_pages: 1,
  });
  // In-table filter — distinct from the ad-hoc lookup above (which loads a
  // brand new dashboard). `wlSearch` tracks the input value; `wlSearchActive`
  // is the debounced value actually sent to the backend.
  const [wlSearch, setWlSearch] = useState("");
  const [wlSearchActive, setWlSearchActive] = useState("");
  

  // bump to identify "new search"; charts freeze per version
  const searchVersionRef = useRef(0);

  async function postMetric(metric, query, extra = {}) {
  const jwt = getJwtToken();
  const resp = await fetch(DASHBOARD_API, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    body: JSON.stringify({ query, metric, ...extra }),
  });

         if ([401, 419, 440].includes(resp.status)) {
      redirectToLogin();
      // throw to stop callers from continuing
      throw new Error(`AUTH_REDIRECT_${resp.status}`);
    }

    if (resp.status === 400) {
          const text = await resp.text();
          if (text.includes("Invalid email format")) {
            openModalFor("INVALID_EMAIL");
          }
          else if(text.includes("search limit"))
          {
            openModalFor("SEARCH_LIMIT");
          }
          else {
            openModalFor("GENERIC_ERROR");
          }
        
          return;
        }

  if (!resp.ok) {
    const errText = await safeText(resp);
    throw new Error(`dashboard_stats error ${resp.status}${errText ? `: ${errText.slice(0,120)}` : ""}`);
  }

  const bodyText = await safeText(resp);
  if (!bodyText) return defaultForMetric(metric);

  try {
    return JSON.parse(bodyText);
  } catch {
    return defaultForMetric(metric);
  }

  async function safeText(r) {
    try { return await r.text(); } catch { return ""; }
  }
  function defaultForMetric(m) {
    const zero = 0, empty = [];
    switch (m) {
      case "consumer_count":
      case "corporate_count":
      case "ip_address_count":
      case "computer_count":
        return zero;
      case "consumer_incident_timeline":
      case "corporate_incident_timeline":
      case "latest_consumer_events":
      case "latest_corporate_events":
        return empty;
      default:
        return empty;
    }
  }
}

// Debounce the input — don't hit the backend on every keystroke.
useEffect(() => {
  const t = setTimeout(() => setWlSearchActive(wlSearch.trim()), 250);
  return () => clearTimeout(t);
}, [wlSearch]);

// Any time the active query changes, rewind to page 1. Otherwise the user
// could land on page 5 of a query whose result set is only 1 page long.
useEffect(() => {
  setWlPage(1);
}, [wlSearchActive]);

useEffect(() => {
  let aborted = false;

  async function loadWatchlistDomains() {
    setWlLoading(true);
    setWlError("");

    try {
      const jwtToken = getJwtToken();

      const resp = await fetch(WATCHLISTS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {}),
        },
        body: JSON.stringify({
          metric: "watchlist_domains",
          page: wlPage,
          per_page: WATCHLIST_PAGE_SIZE,
          // Only include `search` when there's an actual query, so the
          // unfiltered call shape stays identical to before.
          ...(wlSearchActive ? { search: wlSearchActive } : {}),
        }),
      });

      if ([401, 419, 440].includes(resp.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${resp.status}`);
      }

      if (!resp.ok) throw new Error(`Request failed: ${resp.status}`);

      const json = await resp.json();

      // Server-paginated shape:
      // {
      //   data: [{ domain, last_event_at, last_corporate_event_at,
      //            last_consumer_event_at, total_leaks }],
      //   pagination: { page, per_page, total, total_pages }
      // }
      // We still accept a bare array for back-compat in case the endpoint
      // is ever swapped back.
      const raw = Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];
      const rows = raw
        .map((r) => {
          if (!r || typeof r.domain !== "string") return null;
          const domain = r.domain.trim();
          if (!domain) return null;
          return {
            id: domain,
            label: domain,
            domain,
            watchlistSince: r.watchlist_since || null,
            lastEventAt: r.last_event_at || null,
            lastCorporateAt: r.last_corporate_event_at || null,
            lastConsumerAt: r.last_consumer_event_at || null,
            totalLeaks: r.total_leaks ?? null,
          };
        })
        .filter(Boolean);

      // Pagination metadata — fall back to deriving from the row count if
      // the endpoint omits it (back-compat with the pre-paginated shape).
      const meta = json && typeof json.pagination === "object" && json.pagination !== null
        ? json.pagination
        : null;
      const pageInfo = {
        page: Number(meta?.page) || wlPage,
        per_page: Number(meta?.per_page) || WATCHLIST_PAGE_SIZE,
        total: Number(meta?.total ?? (Array.isArray(json) ? json.length : rows.length)),
        total_pages: Number(meta?.total_pages) || Math.max(1, Math.ceil(rows.length / WATCHLIST_PAGE_SIZE)),
      };

      if (!aborted) {
        setWatchlistQueries(rows);
        setWlPagination(pageInfo);
      }
    } catch (e) {
      if (!aborted) setWlError(e?.message || "Unable to load watchlist domains");
    } finally {
      if (!aborted) setWlLoading(false);
    }
  }

  loadWatchlistDomains();
  return () => { aborted = true; };
}, [wlPage, wlSearchActive]);


  /* ===== Batched dashboard fetches =====
   *
   * The endpoint now serves the whole dashboard in two batched calls:
   *   tier1 → fast counts (consumer / corporate / ip / computer)
   *   tier2 → heavier charts + latest events
   *
   * Both fire on every search; tier1's response paints the KPI strip in
   * ~200ms, tier2 fills the charts and tables a moment later.
   */
  async function fetchDashboardTier1(q) {
    return postMetric("dashboard", q, { sections: "tier1" });
  }
  async function fetchDashboardTier2(q) {
    return postMetric("dashboard", q, { sections: "tier2" });
  }

  // Parse a value that might be a number, a numeric string, or a v3
  // capped string like "100000+". Returns the floor number plus a
  // `capped` flag so callers can sum cleanly and decide whether to
  // append a "+" to the displayed total.
  function parseCapped(v) {
    if (v == null) return { floor: 0, capped: false };
    if (typeof v === "number") return { floor: v, capped: false };
    const s = String(v).trim();
    if (!s) return { floor: 0, capped: false };
    const m = s.match(/^([\d,]+)\s*(\+)?\s*$/);
    if (!m) return { floor: 0, capped: false };
    const floor = Number(m[1].replace(/,/g, "")) || 0;
    return { floor, capped: m[2] === "+" };
  }

  // Sum a list of `{ floor, capped }` objects → `{ floor, capped }`.
  function sumCapped(parts) {
    let floor = 0;
    let capped = false;
    for (const p of parts) {
      floor += p.floor;
      if (p.capped) capped = true;
    }
    return { floor, capped };
  }

  // Display helper. Renders the floor with `+` if any operand was capped
  // (signals "lower bound, real value may be higher"). Defensive — accepts
  // a `{ floor, capped }` object, a primitive number, a numeric string, or
  // a capped string like "100000+". Anything unrecognised → "0".
  function formatCount(c) {
    if (c == null) return "0";
    // Coerce primitives + numeric strings into the canonical shape.
    if (typeof c !== "object" || Array.isArray(c)) {
      c = parseCapped(c);
    }
    const n = Number(c.floor) || 0;
    if (c.capped) {
      // Trailing digits beyond the cap are noise — show compact format.
      // "100K+" reads cleaner than "100,000+" or "200,134+".
      if (n >= 1_000_000) {
        const m = n / 1_000_000;
        return `${m >= 10 ? Math.round(m) : m.toFixed(1).replace(/\.0$/, "")}M+`;
      }
      if (n >= 1_000) {
        return `${Math.round(n / 1000)}K+`;
      }
      return `${n}+`;
    }
    return n.toLocaleString();
  }

  // Read a v3 split-count object → { floor, capped }. Accepts the legacy
  // envelope `{ stealer_<kind>_count, combolist_<kind>_count,
  // database_<kind>_count }` (sums them), a single capped string, or a
  // bare number.
  function readSplitCount(v, kind) {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const present =
        Object.prototype.hasOwnProperty.call(v, `stealer_${kind}_count`) ||
        Object.prototype.hasOwnProperty.call(v, `combolist_${kind}_count`) ||
        Object.prototype.hasOwnProperty.call(v, `database_${kind}_count`);
      if (present) {
        return sumCapped([
          parseCapped(v[`stealer_${kind}_count`]),
          parseCapped(v[`combolist_${kind}_count`]),
          parseCapped(v[`database_${kind}_count`]),
        ]);
      }
    }
    return parseCapped(v);
  }

  function readEventsArray(v) {
    if (Array.isArray(v)) return v;
    if (v && typeof v === "object") return v.items || v.data || [];
    return [];
  }

  const anyLoading = Object.values(loading).some(Boolean);
  const [navigateQuery, setNavigateQuery] = useState("");
  // Accept optional overrideQuery (e.g., from Watchlist)
// Replace your handleSearch with this safe version
const handleSearch = async (arg) => {
  // If arg is a React event (click/keyboard), ignore it as override
  const isEventLike =
    arg &&
    typeof arg === "object" &&
    ("preventDefault" in arg || "nativeEvent" in arg || "type" in arg);

  const overrideQuery = !isEventLike && typeof arg === "string" ? arg : undefined;

  const raw = overrideQuery ?? query ?? "";
  const q = String(raw).trim();
  if (!q) return;

  // keep input + state in sync when coming from Watchlist
  if (overrideQuery && overrideQuery !== query) {
    setQuery(""); // keep your previous behavior
    setSelectedWatchlist(overrideQuery);
    setNavigateQuery(overrideQuery);
  } else {
    setNavigateQuery(q);
    setSelectedWatchlist(null);
  }

  setHasSearchQuery(true);
  setDashError("");

  searchVersionRef.current += 1;
  corpChartRef.current = [];
  consChartRef.current = [];
  setChartTick((t) => t + 1);

  // Flag every count card as loading while tier1 is in flight,
  // every chart / events list while tier2 is in flight. Loading off
  // happens in each tier's .finally.
  const TIER1_KEYS = ["consumerCount", "corporateCount"];
  const TIER2_KEYS = ["consTL", "corpTL", "latestCE", "latestCoE"];
  TIER1_KEYS.forEach((k) => setL(k, true));
  TIER2_KEYS.forEach((k) => setL(k, true));

  // Fire both requests immediately — they run in parallel. We await tier1
  // first so the KPI strip paints as soon as it lands (~200ms), then await
  // tier2 to fill in the charts and events (~1s). Same pattern the API
  // contract was designed for.
  const tier1Promise = fetchDashboardTier1(q);
  const tier2Promise = fetchDashboardTier2(q);

  tier1Promise
    .then((payload) => {
      const s = payload?.sections || payload || {};

      // Top-line KPI cards
      setConsumerCount(readSplitCount(s.consumer_count, "consumer"));
      setCorporateCount(readSplitCount(s.corporate_count, "corporate"));

      // Single-key envelope unwrap — backend sometimes wraps scalars
      // (`{ application_count: 84 }` or `{ dark_web_mentions_count: "100000+" }`).
      const unwrap = (obj, key) =>
        obj && typeof obj === "object" && !Array.isArray(obj) && key in obj
          ? obj[key]
          : obj;

      // Source-level breakdown for the Recent Exposure card. Combines
      // consumer + corporate per source — capped flag propagates if
      // either side hits the 100K limit.
      const cc = s.consumer_count || {};
      const corc = s.corporate_count || {};
      setExposureBreakdown({
        combolist: sumCapped([
          parseCapped(cc.combolist_consumer_count),
          parseCapped(corc.combolist_corporate_count),
        ]),
        malware: sumCapped([
          parseCapped(cc.stealer_consumer_count),
          parseCapped(corc.stealer_corporate_count),
        ]),
        database: sumCapped([
          parseCapped(cc.database_consumer_count),
          parseCapped(corc.database_corporate_count),
        ]),
      });

      setApplicationCount(parseCapped(unwrap(s.application_count, "application_count")));
      setThirdPartyApplicationCount(
        parseCapped(unwrap(s.third_party_application_count, "third_party_application_count"))
      );
      setDarkWebMentionsCount(
        parseCapped(unwrap(s.dark_web_mentions_count, "dark_web_mentions_count"))
      );
    })
    .catch((err) => setDashError(err?.message || "dashboard tier1 error"))
    .finally(() => TIER1_KEYS.forEach((k) => setL(k, false)));

  tier2Promise
    .then((payload) => {
      const s = payload?.sections || payload || {};

      const consRawTL = pickArray(s.consumer_incident_timeline);
      const corpRawTL = pickArray(s.corporate_incident_timeline);
      const consTL = combineTimeline(consRawTL);
      const corpTL = combineTimeline(corpRawTL);
      setConsumerIncidentTimeline(consTL);
      setCorporateIncidentTimeline(corpTL);
      setConsRaw(consRawTL);
      setCorpRaw(corpRawTL);
      if (consChartRef.current.length === 0 && consTL.length) {
        consChartRef.current = consTL;
        setChartTick((t) => t + 1);
      }
      if (corpChartRef.current.length === 0 && corpTL.length) {
        corpChartRef.current = corpTL;
        setChartTick((t) => t + 1);
      }

      setLatestConsumerEvents(readEventsArray(s.latest_consumer_events));
      setLatestCorporateEvents(readEventsArray(s.latest_corporate_events));
      setLatestDarkWebMentions(readEventsArray(s.latest_dark_web_mentions));
    })
    .catch((err) => setDashError(err?.message || "dashboard tier2 error"))
    .finally(() => TIER2_KEYS.forEach((k) => setL(k, false)));
};

// Ad-hoc search wrapper — same as handleSearch but also records a minimal
// metadata object so the detail header shows at least the domain string.
// (Watchlist row clicks set the full row directly.)
const submitAdHocSearch = () => {
  const v = (query || "").trim();
  if (!v) return;
  // Push the domain into the URL so refreshes / sharable links re-load
  // the same view. The mount effect below picks it up from the route.
  navigate(`/dashboard/${encodeURIComponent(v)}`);
};

// Pick a watchlist row → mirror to URL + carry the row metadata.
const pickWatchlistRow = (row) => {
  setSelectedDomainMeta(row);
  navigate(`/dashboard/${encodeURIComponent(row.domain)}`);
};

// Back to the watchlist landing — wipe selection + force the empty-state
// branch to re-render the table. Clearing the URL drops the :domain param.
const handleBackToWatchlist = () => {
  setSelectedDomainMeta(null);
  setSelectedWatchlist(null);
  setHasSearchQuery(null);
  setQuery("");
  setNavigateQuery("");
  navigate("/dashboard");
};

// Sync route → state. Fires on mount + any time :domain changes (e.g. a
// pickWatchlistRow → navigate, or back/forward buttons).
useEffect(() => {
  const decoded = routeDomain ? decodeURIComponent(routeDomain) : "";
  if (decoded) {
    // Only refire if the route is requesting a different domain than the
    // one we're already showing — prevents a re-fetch loop when state and
    // URL get in sync from the same click.
    if (decoded !== navigateQuery) {
      // Preserve any row metadata we already have (set by pickWatchlistRow
      // before navigation); otherwise fall back to a minimal record stamped
      // with the time we searched — so ad-hoc lookups show "Searched at:"
      // in place of the watchlist metadata strip.
      setSelectedDomainMeta((prev) =>
        prev && prev.domain === decoded
          ? prev
          : { domain: decoded, searchedAt: new Date().toISOString() }
      );
      handleSearch(decoded);
    }
  } else if (hasSearchQuery) {
    // URL dropped back to /dashboard — clear detail state.
    setSelectedDomainMeta(null);
    setSelectedWatchlist(null);
    setHasSearchQuery(null);
    setNavigateQuery("");
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [routeDomain]);

// Watch for fetch failures in detail view → show an error modal and
// kick the user back to the watchlist landing. Without this, a single
// failed metric leaves the dashboard partially populated and silent.
useEffect(() => {
  if (dashError && hasSearchQuery) {
    setErrorModal({
      domain: navigateQuery || selectedDomainMeta?.domain || "",
      message: dashError,
    });
    // Bounce to landing state + URL.
    setSelectedDomainMeta(null);
    setSelectedWatchlist(null);
    setHasSearchQuery(null);
    setNavigateQuery("");
    setDashError("");
    navigate("/dashboard");
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [dashError]);



  // Trends (based on live timelines; charts are frozen separately)
  const totalTimeline = useMemo(
    () => combineTwoTimelines(consumerIncidentTimeline, corporateIncidentTimeline),
    [consumerIncidentTimeline, corporateIncidentTimeline]
  );
  const totalTrend = computeThisMonthNewRecords(totalTimeline);
  const consumerTrend = computeThisMonthNewRecords(consumerIncidentTimeline);
  const corporateTrend = computeThisMonthNewRecords(corporateIncidentTimeline);

  // Cards — `sparkData` reuses the timeline arrays we already fetch for the
  // trend calc, so adding the inline mini-charts is free of new requests.
  const cardData = [
    {
      key: "total",
      title: "Total Leaks",
      info: "Combined consumer and corporate leaks detected.",
      value: formatCount(sumCapped([consumerCount, corporateCount])),
      trend: totalTrend.trendUi,
      loading: loading.consumerCount || loading.corporateCount,
      viewAll: false,
      sparkData: totalTimeline,
      sparkColor: "#94a3b8",
    },
    {
      key: "consumer",
      title: "Consumer Leaks",
      info: "Total exposed consumer credentials for queried asset.",
      value: formatCount(consumerCount),
      trend: consumerTrend.trendUi,
      loading: loading.consumerCount,
      viewAll: true,
      sparkData: consumerIncidentTimeline,
      sparkColor: "#60a5fa",
    },
    {
      key: "corporate",
      title: "Corporate Leaks",
      info: "Total exposed corporate credentials for queried asset.",
      value: formatCount(corporateCount),
      trend: corporateTrend.trendUi,
      loading: loading.corporateCount,
      viewAll: true,
      sparkData: corporateIncidentTimeline,
      sparkColor: "#facc15",
    },
    {
      key: "darkweb",
      title: "Dark Web Mentions",
      info: "Recent dark-web chatter referencing this asset.",
      value: formatCount(darkWebMentionsCount),
      trend: null,
      loading: loading.consumerCount || loading.corporateCount, // shares the tier1 load window
      viewAll: false,
      sparkData: [], // No timeline series for mentions — sparkline draws flat
      sparkColor: "#8b5cf6", // violet — distinct from credential-side metrics
    },
  ];

  


function ymKey(y, m) { return `${y}-${String(m).padStart(2, "0")}`; }
function nowYM(d = new Date()) { return { y: d.getFullYear(), m: d.getMonth() + 1 }; }
function prevYM(y, m) { m -= 1; if (m === 0) { m = 12; y -= 1; } return { y, m }; }

// Build map: { "YYYY-MM": { stealer: n, combolist: n } } from raw timeline
// raw = [{Year, Month, Count, source}, ...]
function buildMonthlySourceTotals(raw = []) {
  const totals = new Map();
  for (const row of raw) {
    const y = Number(row?.Year), m = Number(row?.Month);
    if (!y || !m) continue;
    const k = ymKey(y, m);
    const src = String(row?.source || '').toLowerCase();
    const cnt = Number(row?.Count) || 0;
    if (!['stealer','combolist'].includes(src)) continue;

    if (!totals.has(k)) totals.set(k, { stealer: 0, combolist: 0 });
    const bucket = totals.get(k);
    bucket[src] += cnt;
  }
  return totals; // Map<YYYY-MM, {stealer, combolist}>
}

function combineMonthlyMaps(mapA, mapB) {
  const out = new Map();
  const add = (k, obj) => {
    if (!out.has(k)) out.set(k, { stealer: 0, combolist: 0 });
    out.get(k).stealer   += obj.stealer   || 0;
    out.get(k).combolist += obj.combolist || 0;
  };

  for (const [k, v] of (mapA || [])) add(k, v);
  for (const [k, v] of (mapB || [])) add(k, v);
  return out;
}

function getThisAndLastMonthTotals(monthMap, now = new Date()) {
  const { y, m } = nowYM(now);
  const thisMonth = getMonthTotals(monthMap, y, m);
  const p = prevYM(y, m);
  const lastMonth = getMonthTotals(monthMap, p.y, p.m);
  return { thisMonth, lastMonth };
}

function getMonthTotals(monthMap, y, m) {
  const k = ymKey(y, m);
  const v = monthMap.get(k);
  return v ? { ...v } : { stealer: 0, combolist: 0 };
}



// 1) Build per-source monthly totals for each
const consMap = buildMonthlySourceTotals(consRaw);
const corpMap = buildMonthlySourceTotals(corpRaw);
// 2) Combine (consumer + corporate)
const combinedMap = combineMonthlyMaps(consMap, corpMap);

// 3) Pull this month + last month (for each source)
const { thisMonth, lastMonth } = getThisAndLastMonthTotals(combinedMap);

// Now you have:
const thisMonthCombolist = thisMonth.combolist; // e.g., 333 from your sample
const thisMonthStealer   = thisMonth.stealer;   // e.g., 61


  function RecentExposureCard({
    breakdown = {
      combolist: { floor: 0, capped: false },
      malware: { floor: 0, capped: false },
      database: { floor: 0, capped: false },
    },
    applications = 0,
    thirdPartyApps = 0,
    loading = false,
  }) {
    // Palette tuned for SOC-style dashboards: desaturated, darker hues.
    // The trio reads as "warm/neutral/cool" (amber → slate → teal) instead
    // of a Tailwind rainbow — closer to how Recorded Future / Mandiant
    // present categorical breakdowns.
    //
    // Each `value` is a `{ floor, capped }` object. Bar widths and
    // percentages use `floor` (an underestimate when capped, but the
    // only honest number we have). Display labels go through formatCount
    // so capped values render as e.g. "100,000+".
    // Normalise every incoming source value through parseCapped so a
    // primitive (legacy) or missing entry can't poison the math with
    // NaN / undefined. After this, every `value` is { floor, capped }.
    const norm = (v) =>
      v && typeof v === "object" && "floor" in v ? v : parseCapped(v);
    const sources = [
      { key: "combolist", label: "Combolist",         value: norm(breakdown?.combolist), color: "#b45309" }, // amber-700
      { key: "malware",   label: "Malware",           value: norm(breakdown?.malware),   color: "#9f1239" }, // rose-800
      { key: "database",  label: "Database breaches", value: norm(breakdown?.database),  color: "#0e7490" }, // cyan-700
    ];
    const total = sources.reduce((acc, s) => acc + (Number(s.value?.floor) || 0), 0);
    const pct = (v) => {
      if (!(total > 0)) return 0;
      const f = Number(v?.floor) || 0;
      const r = (f / total) * 100;
      return Number.isFinite(r) ? r : 0;
    };

    return (
      <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 flex flex-col overflow-hidden h-full">
        {/* Header strip — matches AreaCard / EventsWideCard */}
        <div className="px-4 py-2.5 border-b border-sky-900/30 bg-[#0b1426]/60 flex items-center justify-between gap-3">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
            Exposure breakdown
          </span>
          <span className="text-[10.5px] text-neutral-500 normal-case tracking-normal hidden sm:block">
            Combined by source
          </span>
        </div>

        <div className="p-4 flex flex-col gap-3">
          {/* Per-source rows — label / value / proportional mini-bar / %.
              Single chart, no redundant top stacked bar. */}
          <div className="flex flex-col gap-2.5">
            {sources.map((s) => {
              const share = pct(s.value);
              return (
                <div key={s.key} className="grid grid-cols-[1fr_auto_72px_42px] items-center gap-3 text-[12px]">
                  <span className="inline-flex items-center gap-2 min-w-0 text-neutral-300">
                    <span
                      className="inline-block h-2 w-2 rounded-sm shrink-0"
                      style={{ background: s.color }}
                    />
                    <span className="truncate">{s.label}</span>
                  </span>
                  <span className="text-neutral-100 font-medium tabular-nums">
                    {loading ? (
                      <span className="inline-block h-3 w-12 bg-neutral-800/80 rounded animate-pulse align-middle" />
                    ) : (
                      formatCount(s.value)
                    )}
                  </span>
                  <div className="h-1 rounded-sm bg-neutral-800/70 overflow-hidden">
                    <div
                      className="h-full transition-all"
                      style={{ width: `${share}%`, backgroundColor: s.color }}
                    />
                  </div>
                  <span className="text-[11px] text-neutral-500 tabular-nums text-right">
                    {loading ? "—" : `${share.toFixed(share >= 10 ? 0 : 1)}%`}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Exposed assets — separate concept (count of unique apps
              touched, not exposure records). Sits in its own bordered
              strip so the breakdown above stays scannable. */}
          <div className="mt-1 grid grid-cols-2 gap-3">
            <div className="rounded-md border border-sky-900/30 bg-[#06101e] px-3 py-2.5">
              <div className="text-[10.5px] uppercase tracking-wider text-neutral-500">
                Exposed applications
              </div>
              <div className="mt-1 text-[20px] leading-none font-semibold text-neutral-100 tabular-nums">
                {loading ? (
                  <span className="inline-block h-5 w-12 bg-neutral-800/80 rounded animate-pulse align-middle" />
                ) : (
                  formatCount(applications)
                )}
              </div>
            </div>
            <div className="rounded-md border border-sky-900/30 bg-[#06101e] px-3 py-2.5">
              <div className="text-[10.5px] uppercase tracking-wider text-neutral-500">
                Exposed third-party apps
              </div>
              <div className="mt-1 text-[20px] leading-none font-semibold text-neutral-100 tabular-nums">
                {loading ? (
                  <span className="inline-block h-5 w-12 bg-neutral-800/80 rounded animate-pulse align-middle" />
                ) : (
                  formatCount(thirdPartyApps)
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-auto px-4 py-2 border-t border-sky-900/30 text-[10.5px] text-neutral-500">
          Updated from consumer &amp; corporate event feeds
        </div>
      </div>
    );
  }

  // Events card — sized to share a row with RecentExposureCard (no col-span).
  // Same header-strip chrome as AreaCard for visual continuity.
  // Recent dark-web chatter referencing the queried asset. Single column
  // list — title + relative date, max 5 entries (backend cap). Matches the
  // header-strip chrome of the other panels for visual continuity.
  function LatestDarkWebMentionsCard({ mentions = [], loading = false }) {
    return (
      <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 flex flex-col overflow-hidden h-full">
        <div className="px-4 py-2.5 border-b border-sky-900/30 bg-[#0b1426]/60 flex items-center justify-between gap-3">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
            Latest Dark Web Mentions
          </span>
          <button
            type="button"
            onClick={() => navigate("/threat-feeds")}
            className="text-[11px] text-sky-400 hover:text-sky-300 transition-colors"
          >
            View feed →
          </button>
        </div>

        <ul className="divide-y divide-sky-900/20 flex-1 min-h-0 overflow-y-auto">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <li key={`dw-s-${i}`} className="px-4 py-2.5">
                <div className="h-3 w-3/4 bg-neutral-800/80 rounded animate-pulse" />
                <div className="mt-1.5 h-2 w-20 bg-neutral-800/80 rounded animate-pulse" />
              </li>
            ))
          ) : mentions.length === 0 ? (
            <li className="px-4 py-10">
              <div className="flex flex-col items-center text-center gap-1.5">
                <div className="text-[10.5px] uppercase tracking-wider text-neutral-500 font-medium">
                  No mentions found
                </div>
                <div className="text-[12px] text-neutral-400 leading-relaxed max-w-[220px]">
                  No dark-web chatter has been observed referencing this asset.
                  New mentions will appear here as they're picked up.
                </div>
              </div>
            </li>
          ) : (
            mentions.slice(0, 5).map((m, i) => {
              const typeLabel = String(m.type || "").trim();
              return (
                <li
                  key={i}
                  className="px-4 py-2.5 hover:bg-sky-900/15 transition-colors group cursor-pointer"
                  onClick={() => navigate("/threat-feeds")}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate("/threat-feeds");
                    }
                  }}
                >
                  <div className="text-[12.5px] text-neutral-100 group-hover:text-white leading-snug line-clamp-2" title={m.title}>
                    {m.title || "(untitled)"}
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 text-[10.5px] text-neutral-500">
                    {typeLabel && (
                      <span className="inline-flex items-center rounded border border-violet-500/30 bg-violet-500/10 px-1.5 py-0.5 text-[9.5px] uppercase tracking-wider font-medium text-violet-200">
                        {typeLabel}
                      </span>
                    )}
                    <span className="tabular-nums">
                      {(() => {
                        if (!m.date) return "—";
                        const parsable = typeof m.date === "string" && /^\d{4}-\d{2}-\d{2} /.test(m.date)
                          ? m.date.replace(" ", "T") + "Z"
                          : m.date;
                        const t = Date.parse(parsable);
                        if (Number.isNaN(t)) return "—";
                        const diffMs = Date.now() - t;
                        const min = Math.floor(diffMs / 60000);
                        const hr = Math.floor(min / 60);
                        const day = Math.floor(hr / 24);
                        if (min < 60) return `${Math.max(1, min)}m ago`;
                        if (hr < 24) return `${hr}h ago`;
                        if (day < 30) return `${day}d ago`;
                        if (day < 365) return `${Math.floor(day / 30)}mo ago`;
                        return `${Math.floor(day / 365)}y ago`;
                      })()}
                    </span>
                  </div>
                </li>
              );
            })
          )}
        </ul>
      </div>
    );
  }

  function EventsWideCard({ consumerEvents = [], corporateEvents = [], activeTab, setActiveTab, loading }) {
    const eventsAreLoading = loading.latestCE || loading.latestCoE;
    const rows = activeTab === "consumer" ? consumerEvents : corporateEvents;

    return (
      <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 flex flex-col overflow-hidden h-full">
        {/* Header strip + segmented tab control */}
        <div className="px-4 py-2 border-b border-sky-900/30 bg-[#0b1426]/60 flex items-center justify-between gap-3">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
            Latest Credential Exposures
          </span>
          <div className="inline-flex rounded-md border border-sky-900/40 overflow-hidden">
            <button
              type="button"
              onClick={() => setActiveTab("consumer")}
              className={`px-2.5 py-1 text-[12px] transition-colors border-r border-sky-900/40 ${
                activeTab === "consumer"
                  ? "bg-sky-500/15 text-sky-100 font-medium"
                  : "text-neutral-400 hover:text-neutral-200 hover:bg-white/[0.03]"
              }`}
            >
              Consumer
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("corporate")}
              className={`px-2.5 py-1 text-[12px] transition-colors ${
                activeTab === "corporate"
                  ? "bg-sky-500/15 text-sky-100 font-medium"
                  : "text-neutral-400 hover:text-neutral-200 hover:bg-white/[0.03]"
              }`}
            >
              Corporate
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <table className="w-full text-[12.5px] text-neutral-300">
            <thead className="sticky top-0 z-10 bg-[#0a1224]/95">
              <tr className="border-b border-sky-900/30 text-neutral-500">
                <th className="text-left py-2 px-3 font-medium uppercase tracking-wider text-[10.5px]">Application</th>
                <th className="text-left py-2 px-3 font-medium uppercase tracking-wider text-[10.5px]">Type</th>
                <th className="text-left py-2 px-3 font-medium uppercase tracking-wider text-[10.5px]">Date</th>
                <th className="text-right py-2 px-3 font-medium uppercase tracking-wider text-[10.5px]">Records</th>
              </tr>
            </thead>
            <tbody>
              {eventsAreLoading ? (
                <TableSkeletonRows rows={6} />
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-8 px-3 text-center text-neutral-500 text-[12.5px]">
                    No events found for this category.
                  </td>
                </tr>
              ) : (
                rows.map((ev, idx) => (
                  <tr
                    key={idx}
                    onClick={() => {
                      // Pivot — open Global Search filtered to this application.
                      const q = encodeURIComponent(ev.application || "");
                      if (q) navigate(`/search?query=${q}`);
                    }}
                    className="group border-b border-sky-900/20 last:border-b-0 hover:bg-sky-900/15 cursor-pointer transition-colors"
                  >
                    <td className="py-2 px-3 text-neutral-100 group-hover:text-sky-200 truncate max-w-[180px]" title={ev.application}>
                      {ev.application}
                    </td>
                    <td className="py-2 px-3">
                      <span
                        className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10.5px] font-medium capitalize ring-1 ring-inset ${typePillClass(ev.type)}`}
                      >
                        {ev.type}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-neutral-400 tabular-nums">
                      {ev.date?.slice(0, 10) || ev.date}
                    </td>
                    <td className="py-2 px-3 text-right text-sky-300 tabular-nums">
                      +{(ev.records ?? 0).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white">
      {hasSearchQuery ? (
        /* Domain detail view — back bar on top, then the scoped domain
            header with metadata (same pattern as Supply Chain detail). */
        <>
          <div
            className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-50"
            style={{ marginLeft: collapsed ? "56px" : "224px" }}
          >
            <div className="px-6 h-10 flex items-center">
              <button
                type="button"
                onClick={handleBackToWatchlist}
                className="inline-flex items-center gap-1.5 text-[12px] text-neutral-400 hover:text-sky-300 transition-colors"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Back
              </button>
            </div>
          </div>

          <header
            className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
            style={{ marginLeft: collapsed ? "56px" : "224px" }}
          >
            <div className="px-6 py-4 flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <h1 className="flex items-center gap-2.5 text-[22px] font-semibold leading-tight tracking-tight text-neutral-100">
                  <DomainFavicon
                    domain={selectedDomainMeta?.domain || navigateQuery}
                    size={22}
                  />
                  <span className="min-w-0 break-all">
                    {selectedDomainMeta?.domain || navigateQuery || "—"}
                  </span>
                </h1>
                {selectedDomainMeta && (
                  selectedDomainMeta.watchlistSince ||
                  selectedDomainMeta.lastEventAt ||
                  selectedDomainMeta.lastCorporateAt ||
                  selectedDomainMeta.lastConsumerAt ||
                  selectedDomainMeta.totalLeaks
                ) ? (
                  <div className="mt-2 flex items-center gap-x-4 gap-y-1 flex-wrap text-[12px]">
                    {selectedDomainMeta.lastEventAt && (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-neutral-500">Last event:</span>
                        <span className="text-neutral-200 font-medium">
                          <RelativeAndAbsolute iso={selectedDomainMeta.lastEventAt} />
                        </span>
                      </span>
                    )}
                    {selectedDomainMeta.lastCorporateAt && (
                      <>
                        <span className="text-neutral-700" aria-hidden>·</span>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="text-neutral-500">Newest corporate record:</span>
                          <span className="text-neutral-200 font-medium">
                            <RelativeAndAbsolute iso={selectedDomainMeta.lastCorporateAt} muted />
                          </span>
                        </span>
                      </>
                    )}
                    {selectedDomainMeta.lastConsumerAt && (
                      <>
                        <span className="text-neutral-700" aria-hidden>·</span>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="text-neutral-500">Newest consumer record:</span>
                          <span className="text-neutral-200 font-medium">
                            <RelativeAndAbsolute iso={selectedDomainMeta.lastConsumerAt} muted />
                          </span>
                        </span>
                      </>
                    )}
                    {selectedDomainMeta.watchlistSince && (
                      <>
                        <span className="text-neutral-700" aria-hidden>·</span>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="text-neutral-500">Watchlisted since:</span>
                          <span className="text-neutral-200 font-medium">
                            <RelativeAndAbsolute iso={selectedDomainMeta.watchlistSince} muted />
                          </span>
                        </span>
                      </>
                    )}
                    {selectedDomainMeta.totalLeaks != null && (
                      <>
                        <span className="text-neutral-700" aria-hidden>·</span>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="text-neutral-500">Total leaks:</span>
                          <span className="text-neutral-100 font-semibold tabular-nums">
                            {selectedDomainMeta.totalLeaks}
                          </span>
                        </span>
                      </>
                    )}
                  </div>
                ) : selectedDomainMeta?.searchedAt ? (
                  // Ad-hoc lookup — no watchlist metadata, so surface
                  // when the search was performed instead.
                  <div className="mt-2 text-[12px] text-neutral-500">
                    Searched at:{" "}
                    <span className="text-neutral-300 tabular-nums">
                      {new Date(selectedDomainMeta.searchedAt).toLocaleString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          </header>
        </>
      ) : (
        <header
          className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
          style={{ marginLeft: collapsed ? "56px" : "224px" }}
        >
          <div className="px-6 h-14 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <h1 className="text-[20px] font-bold leading-tight tracking-tight">Dashboard</h1>
            </div>
          </div>
        </header>
      )}


      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey={active}
          onChange={setActive}
        />

        <main className="flex-1 flex flex-col min-w-0">
          {/* Ad-hoc lookup — only shown on the watchlist landing view AND
              only when the watchlist has entries. When the watchlist is
              empty, the hero search inside the table is the single primary
              action — no need to duplicate it in the toolbar above.
              Hidden in detail view since the back bar + domain header
              already convey scope; analysts use Back to pivot. */}
          {!hasSearchQuery && (wlLoading || watchlistQueries.length > 0 || wlSearchActive) && (
            <div className="px-4 md:px-6 pt-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] uppercase tracking-wider text-neutral-500 font-medium shrink-0">
                  Ad-hoc lookup
                </span>
                <div className="relative w-96">
                  <svg className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-500" viewBox="0 0 24 24" fill="currentColor">
                    <path fillRule="evenodd" d="M10 2a8 8 0 105.293 14.293l4.147 4.147a1 1 0 001.414-1.414l-4.147-4.147A8 8 0 0010 2zm-6 8a6 6 0 1112 0 6 6 0 01-12 0z" clipRule="evenodd" />
                  </svg>
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") submitAdHocSearch(); }}
                    placeholder="Domain to scope the dashboard (e.g. acme.com)"
                    inputMode="search"
                    className="w-full rounded-md border border-sky-900/40 bg-[#06101e] pl-8 pr-3 py-1.5 text-[12.5px] text-neutral-100 placeholder:text-neutral-500 outline-none focus:border-sky-500/60 transition-colors"
                  />
                </div>

                <button
                  type="button"
                  onClick={submitAdHocSearch}
                  disabled={!query.trim()}
                  className="inline-flex items-center rounded-md border border-sky-600/40 bg-sky-500/15 hover:bg-sky-500/25 px-3 py-1.5 text-[12px] font-medium text-sky-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Load
                </button>
              </div>
            </div>
          )}


          {hasSearchQuery ? (
            <>
              {/* KPI strip — four top-line metrics share one row on lg+. */}
              <div className="px-4 md:px-6 pt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {cardData.map((card) => (
                  <StatCard
                    key={card.key}
                    title={card.title}
                    info={card.info}
                    value={card.value}
                    trend={card.trend}
                    loading={card.loading}
                    viewAll={card.viewAll}
                    onViewAll={() => onViewAll(card.key)}
                    sparkData={card.sparkData}
                    sparkColor={card.sparkColor}
                  />
                ))}
              </div>

              {/* Middle row — exposure breakdown, latest events, dark web
                  mentions. 12-col grid so we can weight the columns: the
                  events table is the densest so it gets 5, exposure 4,
                  dark-web mentions 3 (each entry is short). */}
              <div className="px-4 md:px-6 pt-3 grid grid-cols-1 lg:grid-cols-12 gap-3">
                <div className="lg:col-span-4">
                  <RecentExposureCard
                    breakdown={exposureBreakdown}
                    applications={applicationCount}
                    thirdPartyApps={thirdPartyApplicationCount}
                    loading={loading.consumerCount || loading.corporateCount}
                  />
                </div>
                <div className="lg:col-span-5">
                  <EventsWideCard
                    consumerEvents={latestConsumerEvents}
                    corporateEvents={latestCorporateEvents}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    loading={loading}
                  />
                </div>
                <div className="lg:col-span-3">
                  <LatestDarkWebMentionsCard
                    mentions={latestDarkWebMentions}
                    loading={loading.latestCE || loading.latestCoE}
                  />
                </div>
              </div>

              {/* Incident Timeline — one chart, two series. Analysts
                  compare corporate vs consumer on the same X axis. */}
              <div className="px-4 md:px-6 pt-3 pb-8">
                <IncidentTimelineCard
                  corporateData={corpChartRef.current}
                  consumerData={consChartRef.current}
                  loading={loading.corpTL || loading.consTL}
                />
              </div>
            </>
          ) : (
            /* Empty state — always renders the table structure so the page
                feels populated even before data lands or when the watchlist
                is empty. Pagination keeps it scannable when the list is
                long. */
            (() => {
              // Server-paginated: rows are exactly one page, totals come
              // from `wlPagination`.
              const totalRows = wlPagination.total;
              const totalPages = Math.max(1, wlPagination.total_pages || 1);
              const safePage = Math.min(Math.max(1, wlPage), totalPages);
              const perPage = wlPagination.per_page || WATCHLIST_PAGE_SIZE;
              const pageRows = watchlistQueries;
              const pageStart = (safePage - 1) * perPage;
              const startIdx = totalRows === 0 ? 0 : pageStart + 1;
              const endIdx = Math.min(pageStart + pageRows.length, totalRows);
              return (
                <div className="px-4 md:px-6 pt-4 pb-10">
                  <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 overflow-hidden">
                    {/* Header strip */}
                    <div className="px-4 py-3 border-b border-sky-900/30 bg-[#0b1426]/60">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
                            Watchlisted domains
                            {totalRows > 0 && (
                              <span className="ml-2 text-neutral-600 normal-case tracking-normal">
                                ({totalRows.toLocaleString()})
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 text-[12px] text-neutral-500">
                            Click a row to scope the dashboard. Use ad-hoc lookup above for any other domain.
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Filter is only meaningful when there's something
                              to filter, or when a query is already active
                              (so it stays around even if the results are 0). */}
                          {(totalRows > 0 || wlSearchActive) && (
                            <div className="relative w-full sm:w-64">
                              <svg className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-500" viewBox="0 0 24 24" fill="currentColor">
                                <path fillRule="evenodd" d="M10 2a8 8 0 105.293 14.293l4.147 4.147a1 1 0 001.414-1.414l-4.147-4.147A8 8 0 0010 2zm-6 8a6 6 0 1112 0 6 6 0 01-12 0z" clipRule="evenodd" />
                              </svg>
                              <input
                                type="search"
                                value={wlSearch}
                                onChange={(e) => setWlSearch(e.target.value)}
                                placeholder="Filter watchlist…"
                                inputMode="search"
                                className="w-full rounded-md border border-sky-900/40 bg-[#06101e] pl-8 pr-7 py-1.5 text-[12px] text-neutral-100 placeholder:text-neutral-500 outline-none focus:border-sky-500/60 transition-colors"
                              />
                              {wlSearch && (
                                <button
                                  type="button"
                                  onClick={() => setWlSearch("")}
                                  aria-label="Clear filter"
                                  className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex h-5 w-5 items-center justify-center rounded text-neutral-500 hover:text-neutral-200 hover:bg-white/[0.06] transition-colors"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => navigate("/watchlists")}
                            className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-sky-900/40 hover:bg-white/[0.04] px-2.5 py-1.5 text-[11.5px] text-neutral-300 hover:text-white transition-colors"
                          >
                            Manage watchlists →
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Loading / error / empty branches live outside the table
                        so the mobile card list and the desktop table share a
                        single branching point. */}
                    {wlLoading ? (
                      <div className="px-4 py-10 text-center text-[12.5px] text-neutral-500">
                        Loading watchlisted domains…
                      </div>
                    ) : wlError ? (
                      <div className="px-4 py-10 text-center text-[12.5px] text-rose-300">
                        {wlError}
                      </div>
                    ) : pageRows.length === 0 && wlSearchActive ? (
                      // Empty results from an active filter — different from
                      // a genuinely empty watchlist, so don't show the hero
                      // first-run state below. Offer a one-tap clear.
                      <div className="px-6 py-14 text-center">
                        <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-medium">
                          No matches
                        </div>
                        <div className="mt-1.5 text-[12.5px] text-neutral-400">
                          Nothing in your watchlist matches{" "}
                          <span className="font-mono text-neutral-200">"{wlSearchActive}"</span>.
                        </div>
                        <button
                          type="button"
                          onClick={() => setWlSearch("")}
                          className="mt-3 text-[12px] text-sky-400 hover:text-sky-300 transition-colors"
                        >
                          Clear filter
                        </button>
                      </div>
                    ) : pageRows.length === 0 ? (
                      <div className="px-6 py-16">
                        <div className="flex flex-col items-center gap-4 max-w-md mx-auto text-center">
                          <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-medium">
                            No watchlisted domains yet
                          </div>
                          <h3 className="text-[16px] font-semibold text-neutral-100 leading-snug">
                            Search any domain to load its dashboard
                          </h3>
                          <p className="text-[12.5px] text-neutral-400 leading-relaxed">
                            Look up a vendor ad-hoc, or add domains to your
                            watchlist for one-click access every time you open
                            the Dashboard.
                          </p>

                          {/* Hero search input — larger than the toolbar's so it
                              reads as the primary first-run action. */}
                          <div className="w-full mt-1 flex flex-wrap items-center gap-2">
                            <div className="relative flex-1 min-w-0">
                              <svg className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" viewBox="0 0 24 24" fill="currentColor">
                                <path fillRule="evenodd" d="M10 2a8 8 0 105.293 14.293l4.147 4.147a1 1 0 001.414-1.414l-4.147-4.147A8 8 0 0010 2zm-6 8a6 6 0 1112 0 6 6 0 01-12 0z" clipRule="evenodd" />
                              </svg>
                              <input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") submitAdHocSearch(); }}
                                placeholder="Type a domain (e.g. acme.com)"
                                inputMode="search"
                                autoFocus
                                className="w-full rounded-md border border-sky-900/40 bg-[#06101e] pl-10 pr-3 py-2.5 text-[14px] text-neutral-100 placeholder:text-neutral-500 outline-none focus:border-sky-500/60 focus:ring-1 focus:ring-sky-500/30 transition-colors"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={submitAdHocSearch}
                              disabled={!query.trim()}
                              className="shrink-0 inline-flex items-center rounded-md border border-sky-600/40 bg-sky-500/15 hover:bg-sky-500/25 px-4 py-2.5 text-[13px] font-medium text-sky-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              Load
                            </button>
                          </div>

                          <button
                            type="button"
                            onClick={() => navigate("/watchlists")}
                            className="text-[12px] text-sky-400 hover:text-sky-300 transition-colors"
                          >
                            or build your watchlist →
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Desktop / tablet: 7-column table. */}
                        <div className="hidden md:block overflow-x-auto">
                          <table className="w-full text-[12.5px]">
                            <thead className="bg-[#0b1426]/40 text-neutral-500">
                              <tr className="border-b border-sky-900/30">
                                <th className="px-4 py-2 text-left font-medium uppercase tracking-wider text-[10.5px]">Domain</th>
                                <th className="px-4 py-2 text-left font-medium uppercase tracking-wider text-[10.5px]">Last event</th>
                                <th className="px-4 py-2 text-left font-medium uppercase tracking-wider text-[10.5px]">Newest corporate record</th>
                                <th className="px-4 py-2 text-left font-medium uppercase tracking-wider text-[10.5px]">Newest consumer record</th>
                                <th className="px-4 py-2 text-left font-medium uppercase tracking-wider text-[10.5px]">Watchlisted since</th>
                                <th className="px-4 py-2 text-left font-medium uppercase tracking-wider text-[10.5px]">Total leaks</th>
                                <th className="px-4 py-2 text-right font-medium uppercase tracking-wider text-[10.5px] w-20" />
                              </tr>
                            </thead>
                            <tbody>
                              {pageRows.map((row, i) => (
                                <tr
                                  key={row.id}
                                  onClick={() => pickWatchlistRow(row)}
                                  className={`group cursor-pointer transition-colors border-b border-sky-900/20 last:border-b-0 hover:bg-sky-900/15 ${
                                    i % 2 === 0 ? "" : "bg-[#091020]/30"
                                  }`}
                                >
                                  <td className="px-4 py-2.5">
                                    <span className="inline-flex items-center gap-2 min-w-0">
                                      <DomainFavicon domain={row.domain} size={16} />
                                      <span className="text-sky-300 group-hover:text-sky-200 font-mono">
                                        {row.domain}
                                      </span>
                                    </span>
                                  </td>
                                  <td className="px-4 py-2.5 text-neutral-300 tabular-nums">
                                    <RelativeAndAbsolute iso={row.lastEventAt} />
                                  </td>
                                  <td className="px-4 py-2.5 text-neutral-400 tabular-nums">
                                    <RelativeAndAbsolute iso={row.lastCorporateAt} muted />
                                  </td>
                                  <td className="px-4 py-2.5 text-neutral-400 tabular-nums">
                                    <RelativeAndAbsolute iso={row.lastConsumerAt} muted />
                                  </td>
                                  <td className="px-4 py-2.5 text-neutral-400 tabular-nums">
                                    <RelativeAndAbsolute iso={row.watchlistSince} muted />
                                  </td>
                                  <td className="px-4 py-2.5 text-neutral-100 font-medium tabular-nums">
                                    {row.totalLeaks ?? "—"}
                                  </td>
                                  <td className="px-4 py-2.5 text-right text-[12px] font-medium text-sky-400 group-hover:text-sky-200 transition-colors">
                                    View →
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Mobile: each row becomes a stacked card so the 7-col
                            table doesn't force horizontal scroll. Same data,
                            regrouped: domain + totals on top, then a 2-col grid
                            of freshness + vintage metadata. */}
                        <ul className="md:hidden divide-y divide-sky-900/20">
                          {pageRows.map((row) => (
                            <li
                              key={row.id}
                              onClick={() => pickWatchlistRow(row)}
                              className="group cursor-pointer px-4 py-3 hover:bg-sky-900/15 transition-colors"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <span className="flex items-start gap-2 min-w-0">
                                  <DomainFavicon
                                    domain={row.domain}
                                    size={16}
                                    className="mt-0.5"
                                  />
                                  <span className="text-sky-300 group-hover:text-sky-200 font-mono text-[13px] break-all min-w-0">
                                    {row.domain}
                                  </span>
                                </span>
                                <span className="shrink-0 text-[11px] text-neutral-500">
                                  <span className="text-neutral-100 font-semibold tabular-nums">
                                    {row.totalLeaks ?? "—"}
                                  </span>{" "}
                                  leaks
                                </span>
                              </div>

                              <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-2 text-[11.5px]">
                                <div>
                                  <dt className="text-[9.5px] uppercase tracking-wider text-neutral-500 font-medium">Last event</dt>
                                  <dd className="mt-0.5 text-neutral-200">
                                    <RelativeAndAbsolute iso={row.lastEventAt} />
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-[9.5px] uppercase tracking-wider text-neutral-500 font-medium">Watchlisted since</dt>
                                  <dd className="mt-0.5 text-neutral-400">
                                    <RelativeAndAbsolute iso={row.watchlistSince} muted />
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-[9.5px] uppercase tracking-wider text-neutral-500 font-medium">Newest corporate</dt>
                                  <dd className="mt-0.5 text-neutral-400">
                                    <RelativeAndAbsolute iso={row.lastCorporateAt} muted />
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-[9.5px] uppercase tracking-wider text-neutral-500 font-medium">Newest consumer</dt>
                                  <dd className="mt-0.5 text-neutral-400">
                                    <RelativeAndAbsolute iso={row.lastConsumerAt} muted />
                                  </dd>
                                </div>
                              </dl>

                              <div className="mt-2.5 flex justify-end text-[11.5px] font-medium text-sky-400 group-hover:text-sky-200 transition-colors">
                                View →
                              </div>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}

                    {/* Pagination footer — only shown when we have rows. */}
                    {!wlLoading && !wlError && totalRows > 0 && (
                      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-sky-900/30 px-4 py-2.5">
                        <div className="text-[12px] text-neutral-500">
                          Results: {startIdx} – {endIdx} of {totalRows.toLocaleString()}
                        </div>
                        {totalPages > 1 && (
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => setWlPage((p) => Math.max(1, p - 1))}
                              disabled={safePage <= 1}
                              className="inline-flex items-center justify-center rounded-md border border-sky-900/30 hover:bg-sky-900/30 p-1 text-neutral-400 hover:text-sky-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                              aria-label="Previous page"
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                              <button
                                key={p}
                                type="button"
                                onClick={() => setWlPage(p)}
                                className={`min-w-[28px] h-7 rounded-md text-[12px] font-medium transition-colors ${
                                  p === safePage
                                    ? "bg-sky-500/20 text-sky-200 border border-sky-500/40"
                                    : "text-neutral-400 hover:text-sky-300 hover:bg-sky-900/30 border border-transparent"
                                }`}
                              >
                                {p}
                              </button>
                            ))}
                            <button
                              type="button"
                              onClick={() => setWlPage((p) => Math.min(totalPages, p + 1))}
                              disabled={safePage >= totalPages}
                              className="inline-flex items-center justify-center rounded-md border border-sky-900/30 hover:bg-sky-900/30 p-1 text-neutral-400 hover:text-sky-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                              aria-label="Next page"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()
          )}
        </main>
      </div>
         {cfg && (
                  <FrostedModal
                    open={modal.open}
                    onClose={closeModal}
                    icon={cfg.icon}
                    title={cfg.title}
                    description={cfg.description}
                    primaryAction={
                      cfg.primary && {
                        label: cfg.primary.label,
                        onClick: () => {
                          if (cfg.primary.href) window.location.href = cfg.primary.href;
                          else closeModal();
                        },
                      }
                    }
                    secondaryAction={
                      cfg.secondary && {
                        label: cfg.secondary.label,
                        onClick: closeModal,
                      }
                    }
                  />
                )}

      {/* Dashboard load error — shown when one of the metric fetches fails.
          Branch on quota errors (HTTP 429) to surface an Upgrades CTA. */}
      {(() => {
        if (!errorModal) return null;
        const raw = errorModal.message || "";

        // Pull the inner `error` field out of the API's JSON envelope when
        // present, e.g. `dashboard_stats error 429: {"error":"You have
        // reached the daily limit..."}`. Falls back to the raw message.
        let inner = raw;
        const jsonMatch = raw.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            const obj = JSON.parse(jsonMatch[0]);
            if (typeof obj?.error === "string" && obj.error.trim()) {
              inner = obj.error.trim();
            }
          } catch { /* leave as raw */ }
        }

        const isQuota = /\b429\b|daily limit|upgrade to continue/i.test(raw);

        return (
          <FrostedModal
            open={!!errorModal}
            onClose={() => setErrorModal(null)}
            eyebrow={isQuota ? "Daily limit reached" : "Dashboard error"}
            title={
              isQuota
                ? "You've hit today's global search limit"
                : errorModal.domain
                  ? `Couldn't load the dashboard for ${errorModal.domain}`
                  : "Couldn't load the dashboard"
            }
            description={
              <div className="space-y-3">
                <p className="text-neutral-300">
                  {isQuota
                    ? "Your plan caps the number of dashboard scopes per day. Upgrade for higher (or unlimited) daily searches, or come back tomorrow when the counter resets."
                    : "We hit an error while fetching one of the metrics. You've been returned to the watchlist so you can try again or pick a different domain."}
                </p>
                {inner && !isQuota && (
                  <pre className="rounded-md border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-[12px] text-rose-200 font-mono whitespace-pre-wrap break-words">
                    {inner}
                  </pre>
                )}
                {isQuota && inner && (
                  <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[12.5px] text-amber-200">
                    {inner}
                  </div>
                )}
              </div>
            }
            primaryAction={
              isQuota
                ? {
                    label: "View upgrade plans",
                    onClick: () => {
                      setErrorModal(null);
                      navigate("/upgrades");
                    },
                  }
                : {
                    label: "Dismiss",
                    onClick: () => setErrorModal(null),
                  }
            }
            secondaryAction={
              isQuota
                ? { label: "Dismiss", onClick: () => setErrorModal(null) }
                : undefined
            }
          />
        );
      })()}
    </div>
  );
}
